import { ArrowsRightLeftIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Radio, RadioGroup } from '@heroui/radio';
import { Tab, Tabs } from '@heroui/tabs';
import { addToast } from '@heroui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  getContractsControllerFindAllQueryKey,
  getMergeProposalsControllerListQueryKey,
  getMergeProposalsControllerOverlapsQueryKey,
  useMergeProposalsControllerApprove,
  useMergeProposalsControllerDismiss,
  useMergeProposalsControllerList,
  useMergeProposalsControllerOverlaps,
} from '@/api/generated/contracts/contracts';
import { type MergeProposalDto, MergeProposalDtoSource } from '@/api/generated/schemas';
import { EmptyState, ErrorState, LoadingSpinner, PageHeader } from '@/components/common';
import { formatDate } from '@/utils/formatting';

export const Route = createFileRoute('/contracts/merge-proposals')({
  component: MergeProposalsPage,
});

function MergeProposalsPage() {
  // Post-approve/dismiss refresh is driven by explicit invalidateQueries in
  // ProposalCard, so we don't need staleTime:0 + focus refetching — that just
  // hammered this slow endpoint and a cancelled background refetch would wipe
  // the rendered list.
  const zohoQuery = useMergeProposalsControllerList(undefined, {
    query: { staleTime: 30_000, refetchOnWindowFocus: false },
  });
  const overlapsQuery = useMergeProposalsControllerOverlaps(undefined, {
    query: { staleTime: 30_000, refetchOnWindowFocus: false },
  });

  const zohoProposals = zohoQuery.data?.proposals ?? [];
  const overlapProposals = overlapsQuery.data?.proposals ?? [];

  return (
    <div className="py-6">
      <div className="flex flex-col gap-4">
        <PageHeader
          title="Contract merge proposals"
          description="Review contracts that should be consolidated — Zoho-imported multi-site rollups, and same-type contracts that overlap on a single site (a data error)."
        />

        <Tabs aria-label="Merge proposals">
          <Tab key="zoho" title={`Zoho Merges (${zohoProposals.length})`}>
            <ProposalsList
              isLoading={zohoQuery.isLoading}
              error={zohoQuery.error}
              proposals={zohoProposals}
              emptyTitle="No pending merge proposals"
              emptyDescription="No Zoho-imported contracts currently group into multi-site candidates. Run the import or check back later."
            />
          </Tab>
          <Tab key="overlaps" title={`Site Overlaps (${overlapProposals.length})`}>
            <ProposalsList
              isLoading={overlapsQuery.isLoading}
              error={overlapsQuery.error}
              proposals={overlapProposals}
              emptyTitle="No overlapping contracts"
              emptyDescription="No sites currently have two or more overlapping same-type contracts."
            />
          </Tab>
        </Tabs>
      </div>
    </div>
  );
}

function ProposalsList({
  isLoading,
  error,
  proposals,
  emptyTitle,
  emptyDescription,
}: {
  isLoading: boolean;
  error: unknown;
  proposals: MergeProposalDto[];
  emptyTitle: string;
  emptyDescription: string;
}) {
  if (isLoading) {
    return <LoadingSpinner />;
  }
  // Only fatal when we have nothing to show — a failed background refetch
  // must not wipe an already-rendered list.
  if (error && proposals.length === 0) {
    return <ErrorState title="Failed to load merge proposals" message="Please try again later" />;
  }
  if (proposals.length === 0) {
    return <EmptyState icon={CheckCircleIcon} title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <div className="flex flex-col gap-4 pt-2">
      <p className="text-sm text-default-500">
        {proposals.length} {proposals.length === 1 ? 'proposal' : 'proposals'} found
      </p>
      <div className="flex flex-col gap-4">
        {proposals.map((p) => (
          <ProposalCard key={p.id} proposal={p} />
        ))}
      </div>
    </div>
  );
}

function ProposalCard({ proposal }: { proposal: MergeProposalDto }) {
  const isOverlap = proposal.source === MergeProposalDtoSource.SITE_OVERLAP;
  const queryClient = useQueryClient();
  const approveMutation = useMergeProposalsControllerApprove();
  const dismissMutation = useMergeProposalsControllerDismiss();

  // Default canonical:
  // - Zoho: first contract (deterministic given backend's stable ordering).
  // - Overlap: the contract with the latest endDate (keep the most recent),
  //   falling back to the last contract in the array when endDate is missing.
  const defaultCanonicalId = useMemo(() => {
    if (isOverlap && proposal.contracts.length > 0) {
      const latest = proposal.contracts.reduce(
        (best, c) => {
          if (!c.endDate) return best;
          if (!best.endDate) return c;
          return new Date(c.endDate) > new Date(best.endDate) ? c : best;
        },
        proposal.contracts[proposal.contracts.length - 1],
      );
      return latest.contractId;
    }
    return proposal.contracts[0]?.contractId ?? '';
  }, [isOverlap, proposal.contracts]);

  const [canonicalId, setCanonicalId] = useState<string>(defaultCanonicalId);

  const redundantIds = useMemo(
    () => proposal.contracts.filter((c) => c.contractId !== canonicalId).map((c) => c.contractId),
    [proposal.contracts, canonicalId],
  );

  async function invalidate() {
    if (isOverlap) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getMergeProposalsControllerOverlapsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getMergeProposalsControllerListQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getContractsControllerFindAllQueryKey() }),
      ]);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: getMergeProposalsControllerListQueryKey() });
  }

  async function handleApprove() {
    if (!canonicalId || redundantIds.length === 0) return;
    try {
      const result = await approveMutation.mutateAsync({
        data: { canonicalContractId: canonicalId, redundantContractIds: redundantIds },
      });
      addToast(
        isOverlap
          ? {
              title: 'Resolved',
              description: `Archived ${result.archivedContractIds.length} contract(s).`,
              color: 'success',
            }
          : {
              title: 'Merged',
              description: `Relinked ${result.sitesRelinked} site(s) onto canonical, archived ${result.archivedContractIds.length} record(s).`,
              color: 'success',
            },
      );
      await invalidate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Action failed';
      addToast({ title: isOverlap ? 'Resolve failed' : 'Merge failed', description: message, color: 'danger' });
    }
  }

  async function handleDismiss() {
    try {
      await dismissMutation.mutateAsync({
        data: { contractIds: proposal.contracts.map((c) => c.contractId) },
      });
      addToast({
        title: 'Dismissed',
        description: `${proposal.contracts.length} contract(s) flagged so this proposal will not reappear.`,
        color: 'default',
      });
      await invalidate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Dismiss failed';
      addToast({ title: 'Dismiss failed', description: message, color: 'danger' });
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-1">
        {isOverlap ? (
          <>
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-danger-600" />
              <h3 className="text-base font-semibold">
                Overlapping contracts on site {proposal.nmi ?? proposal.siteId}
              </h3>
              <Chip color="danger" size="sm" variant="flat">
                Data error
              </Chip>
            </div>
            <p className="text-xs text-default-500">
              {proposal.retailer} · {proposal.contractType}
              {proposal.marketSegment ? ` · ${proposal.marketSegment}` : ''}
              {proposal.startDate ? ` · ${formatDate(proposal.startDate)}` : ''}
              {' – '}
              {formatDate(proposal.endDate)}
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="w-5 h-5 text-warning-600" />
              <h3 className="text-base font-semibold">
                {proposal.strataCompanyName ?? proposal.parentAccountName ?? 'Unknown company'}
              </h3>
            </div>
            <p className="text-xs text-default-500">
              {proposal.source === MergeProposalDtoSource.ZOHO_STRATA ? 'Strata' : 'Parent account'} ·{' '}
              {proposal.retailer} · {proposal.contractType}
              {proposal.marketSegment ? ` · ${proposal.marketSegment}` : ''} · ends {formatDate(proposal.endDate)}
              {proposal.startDate ? ` · starts ${formatDate(proposal.startDate)}` : ''}
            </p>
          </>
        )}
      </CardHeader>
      <Divider />
      <CardBody className="gap-4">
        <p className="text-xs text-default-600">
          {isOverlap
            ? `Two or more active ${proposal.contractType} contracts overlap on this site, which shouldn't happen. Pick the one to keep — the others will be archived.`
            : 'Pick the canonical contract (its dates and metadata are preserved). Other contracts have their sites relinked to canonical, then are deleted.'}
        </p>
        <RadioGroup
          value={canonicalId}
          onValueChange={setCanonicalId}
          aria-label={isOverlap ? 'Contract to keep' : 'Canonical contract'}
          classNames={{ wrapper: 'gap-1' }}
        >
          {proposal.contracts.map((c) => (
            <Radio key={c.contractId} value={c.contractId} className="max-w-full">
              <div className="flex flex-col">
                <span className="text-sm">{c.zohoAccountName ?? c.zohoAccountId}</span>
                <span className="text-xs text-default-500">
                  {c.nmi ? `NMI ${c.nmi} · ` : ''}contract {c.contractId}
                  {c.startDate && c.endDate ? ` · ${formatDate(c.startDate)}–${formatDate(c.endDate)}` : ''}
                </span>
              </div>
            </Radio>
          ))}
        </RadioGroup>

        <div className="flex justify-end gap-2 pt-2">
          {!isOverlap && (
            <Button
              variant="light"
              color="default"
              startContent={<XMarkIcon className="w-4 h-4" />}
              isLoading={dismissMutation.isPending}
              onPress={handleDismiss}
            >
              Dismiss
            </Button>
          )}
          <Button
            color={isOverlap ? 'danger' : 'warning'}
            startContent={<ArrowsRightLeftIcon className="w-4 h-4" />}
            isLoading={approveMutation.isPending}
            onPress={handleApprove}
            isDisabled={!canonicalId || redundantIds.length === 0}
          >
            {isOverlap ? 'Resolve (archive others)' : `Merge ${proposal.contracts.length} into 1`}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
